// HRMS — Talent: Hiring, Onboarding, Performance
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import hr from '../api';
import { PageHeader, Btn, Drawer, Input, Select, Textarea, Table, Pill, fmtDate, statusTone, SOFT, PANEL } from '../ui';

const PIPELINE = ['applied', 'screening', 'interview', 'offered', 'hired', 'rejected', 'on_hold'];

// ─── Hiring ────────────────────────────────────────────────────────────────
export function Hiring({ companyId }) {
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [openJ, setOpenJ] = useState(false);
  const [openC, setOpenC] = useState(false);
  const [openConv, setOpenConv] = useState(null);
  const [jForm, setJForm] = useState({ employment_type: 'full_time', openings: 1 });
  const [cForm, setCForm] = useState({});
  const [departments, setDepartments] = useState([]);
  const [convForm, setConvForm] = useState({ joining_date: '', ctc_annual: 0 });

  const reload = async () => {
    if (!companyId) return;
    setJobs(await hr.get('/jobs', { company_id: companyId }));
    setCandidates(await hr.get('/candidates'));
    setDepartments(await hr.get('/departments', { company_id: companyId }));
  };
  useEffect(() => { reload(); }, [companyId]);

  const saveJ = async () => {
    try {
      if (!jForm.title) return toast.error('Title required');
      await hr.post('/jobs', { ...jForm, company_id: companyId, openings: Number(jForm.openings || 1) });
      toast.success('Job posted'); setOpenJ(false); setJForm({ employment_type: 'full_time', openings: 1 }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const saveC = async () => {
    try {
      if (!cForm.first_name || !cForm.job_id) return toast.error('Name and job required');
      await hr.post('/candidates', { ...cForm,
        expected_ctc: Number(cForm.expected_ctc || 0),
        current_ctc: Number(cForm.current_ctc || 0),
        total_experience_years: Number(cForm.total_experience_years || 0),
        skills: (cForm.skills_str || '').split(',').map(s => s.trim()).filter(Boolean) });
      toast.success('Candidate added'); setOpenC(false); setCForm({}); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const move = async (cid, stage) => { await hr.post(`/candidates/${cid}/stage`, { stage }); reload(); };
  const convert = async () => {
    try {
      await hr.post(`/candidates/${openConv.id}/convert`, convForm);
      toast.success('Hired! Employee record created.'); setOpenConv(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="hr-hiring">
      <PageHeader title="Hiring" subtitle="Jobs · candidates · pipeline"
        right={<div className="flex gap-2"><Btn variant="ghost" onClick={() => setOpenJ(true)} data-testid="hr-new-job">+ Post Job</Btn><Btn variant="accent" onClick={() => setOpenC(true)} data-testid="hr-new-candidate">+ Add Candidate</Btn></div>} />

      <div className="mb-6">
        <h3 className="text-white font-medium mb-2 text-sm">Open Jobs ({jobs.filter(j => j.status === 'open').length})</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {jobs.map((j) => (
            <div key={j.id} className={`${SOFT} rounded-xl p-4`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-white font-medium">{j.title}</div>
                  <div className="text-xs text-white/45 mt-0.5">{j.job_no} · {j.openings} openings</div>
                </div>
                <Pill tone={statusTone(j.status)}>{j.status}</Pill>
              </div>
              <div className="text-xs text-white/55 mt-3">Applicants: <span className="text-white tabular-nums">{j.applicants}</span></div>
            </div>
          ))}
          {!jobs.length && <p className="text-white/40 italic text-sm">No jobs posted yet.</p>}
        </div>
      </div>

      <h3 className="text-white font-medium mb-2 text-sm">Candidate Pipeline</h3>
      <Table
        columns={[
          { key: 'full_name', label: 'Candidate', render: (r) => <div><div className="text-white">{r.full_name}</div><div className="text-xs text-white/45">{r.current_designation || ''}</div></div> },
          { key: 'email', label: 'Contact', render: (r) => <span className="text-xs text-white/55">{r.email || r.phone}</span> },
          { key: 'expected_ctc', label: 'Expected CTC', align: 'right', render: (r) => <span className="tabular-nums">₹{(r.expected_ctc || 0).toLocaleString('en-IN')}</span> },
          { key: 'stage', label: 'Stage', render: (r) => (
              <select className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-xs text-white" value={r.stage} onChange={(e) => move(r.id, e.target.value)} data-testid={`cand-stage-${r.id}`}>
                {PIPELINE.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>) },
          { key: 'a', label: '', render: (r) => r.stage === 'offered' ? <button className="text-xs text-emerald-300 hover:text-emerald-200" onClick={() => { setOpenConv(r); setConvForm({ joining_date: '', ctc_annual: r.expected_ctc || 0 }); }} data-testid={`cand-hire-${r.id}`}>Hire →</button> : null },
        ]}
        rows={candidates} empty="No candidates yet."
      />

      <Drawer open={openJ} onClose={() => setOpenJ(false)} title="Post Job" width="max-w-2xl">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Title *" value={jForm.title || ''} onChange={(e) => setJForm({ ...jForm, title: e.target.value })} data-testid="job-title" />
          <Input label="Openings" type="number" value={jForm.openings} onChange={(e) => setJForm({ ...jForm, openings: e.target.value })} />
          <Select label="Department" value={jForm.department_id || ''} onChange={(e) => setJForm({ ...jForm, department_id: e.target.value })}>
            <option value="">—</option>{departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
          <Select label="Employment Type" value={jForm.employment_type} onChange={(e) => setJForm({ ...jForm, employment_type: e.target.value })}>
            <option value="full_time">Full-time</option><option value="part_time">Part-time</option>
            <option value="contract">Contract</option><option value="intern">Intern</option>
          </Select>
          <Input label="Budget Min" type="number" value={jForm.budget_min || 0} onChange={(e) => setJForm({ ...jForm, budget_min: e.target.value })} />
          <Input label="Budget Max" type="number" value={jForm.budget_max || 0} onChange={(e) => setJForm({ ...jForm, budget_max: e.target.value })} />
        </div>
        <Textarea label="Description" value={jForm.description || ''} onChange={(e) => setJForm({ ...jForm, description: e.target.value })} />
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpenJ(false)}>Cancel</Btn><Btn variant="accent" onClick={saveJ}>Post</Btn></div>
      </Drawer>

      <Drawer open={openC} onClose={() => setOpenC(false)} title="Add Candidate" width="max-w-2xl">
        <div className="grid grid-cols-2 gap-3">
          <Select label="For Job *" value={cForm.job_id || ''} onChange={(e) => setCForm({ ...cForm, job_id: e.target.value })}>
            <option value="">—</option>{jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
          </Select>
          <Input label="First Name *" value={cForm.first_name || ''} onChange={(e) => setCForm({ ...cForm, first_name: e.target.value })} />
          <Input label="Last Name" value={cForm.last_name || ''} onChange={(e) => setCForm({ ...cForm, last_name: e.target.value })} />
          <Input label="Email" type="email" value={cForm.email || ''} onChange={(e) => setCForm({ ...cForm, email: e.target.value })} />
          <Input label="Phone" value={cForm.phone || ''} onChange={(e) => setCForm({ ...cForm, phone: e.target.value })} />
          <Input label="Experience (years)" type="number" value={cForm.total_experience_years || 0} onChange={(e) => setCForm({ ...cForm, total_experience_years: e.target.value })} />
          <Input label="Current CTC" type="number" value={cForm.current_ctc || 0} onChange={(e) => setCForm({ ...cForm, current_ctc: e.target.value })} />
          <Input label="Expected CTC" type="number" value={cForm.expected_ctc || 0} onChange={(e) => setCForm({ ...cForm, expected_ctc: e.target.value })} />
          <Input label="Notice Period (days)" type="number" value={cForm.notice_period_days || 30} onChange={(e) => setCForm({ ...cForm, notice_period_days: e.target.value })} />
          <Input label="Skills (comma-separated)" value={cForm.skills_str || ''} onChange={(e) => setCForm({ ...cForm, skills_str: e.target.value })} />
        </div>
        <Textarea label="Notes" value={cForm.notes || ''} onChange={(e) => setCForm({ ...cForm, notes: e.target.value })} />
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpenC(false)}>Cancel</Btn><Btn variant="accent" onClick={saveC}>Save</Btn></div>
      </Drawer>

      <Drawer open={!!openConv} onClose={() => setOpenConv(null)} title={`Hire ${openConv?.full_name || ''}`} width="max-w-lg">
        <div className="space-y-3">
          <Input label="Joining Date *" type="date" value={convForm.joining_date} onChange={(e) => setConvForm({ ...convForm, joining_date: e.target.value })} />
          <Input label="CTC (annual)" type="number" value={convForm.ctc_annual} onChange={(e) => setConvForm({ ...convForm, ctc_annual: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpenConv(null)}>Cancel</Btn><Btn variant="accent" onClick={convert} data-testid="cand-convert-save">Hire & Create Employee</Btn></div>
      </Drawer>
    </div>
  );
}

// ─── Onboarding ────────────────────────────────────────────────────────────
export function Onboarding({ companyId }) {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(null);
  const reload = async () => {
    if (!companyId) return;
    setRows(await hr.get('/onboardings'));
    setEmployees(await hr.get('/employees', { company_id: companyId }));
  };
  useEffect(() => { reload(); }, [companyId]);

  const start = async (eid) => {
    try { await hr.post(`/onboardings/${eid}/start`); toast.success('Onboarding started'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const toggle = async (oid, idx) => {
    await hr.post(`/onboardings/${oid}/task/${idx}/toggle`);
    setOpen(await hr.get('/onboardings').then(r => r.find(x => x.id === oid)));
    reload();
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e]));

  return (
    <div data-testid="hr-onboarding">
      <PageHeader title="Onboarding" subtitle="Joining checklists · auto-tasks"
        right={
          <Select onChange={(e) => e.target.value && start(e.target.value)} defaultValue="">
            <option value="">Start onboarding for…</option>
            {employees.filter(e => !rows.find(r => r.employee_id === e.id)).map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </Select>
        } />
      <Table
        columns={[
          { key: 'employee_name', label: 'Employee', render: (r) => <span className="font-medium text-white">{r.employee_name}</span> },
          { key: 'progress', label: 'Progress', align: 'right', render: (r) => (
              <div className="flex items-center gap-2 justify-end">
                <div className="w-20 h-1.5 bg-white/5 rounded overflow-hidden"><div style={{ width: `${r.progress}%`, backgroundColor: '#1AA84B' }} className="h-full" /></div>
                <span className="tabular-nums text-sm">{r.progress}%</span>
              </div>) },
          { key: 'status', label: 'Status', render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> },
          { key: 'a', label: '', render: (r) => <button className="text-xs text-white/55 hover:text-white" onClick={() => setOpen(r)}>Tasks →</button> },
        ]}
        rows={rows} empty="No onboardings in progress."
      />
      <Drawer open={!!open} onClose={() => setOpen(null)} title={open ? `Onboarding · ${open.employee_name}` : ''} width="max-w-2xl">
        {open && (
          <div className="space-y-2">
            {open.tasks.map((t, i) => (
              <label key={i} className="flex items-start gap-3 bg-white/[0.03] border border-white/10 p-3 rounded-lg cursor-pointer hover:bg-white/[0.05]">
                <input type="checkbox" checked={t.completed} onChange={() => toggle(open.id, i)} className="mt-1" data-testid={`task-${i}`} />
                <div className="flex-1">
                  <div className={`text-sm ${t.completed ? 'text-white/40 line-through' : 'text-white'}`}>{t.title}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-white/35 mt-1">{t.category}</div>
                </div>
              </label>
            ))}
          </div>
        )}
      </Drawer>
    </div>
  );
}

// ─── Performance ───────────────────────────────────────────────────────────
export function Performance({ companyId }) {
  const [goals, setGoals] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [openG, setOpenG] = useState(false);
  const [openCy, setOpenCy] = useState(false);
  const [gForm, setGForm] = useState({ category: 'kra' });
  const [cyForm, setCyForm] = useState({ review_type: 'annual' });

  const reload = async () => {
    if (!companyId) return;
    setGoals(await hr.get('/goals'));
    setCycles(await hr.get('/review-cycles', { company_id: companyId }));
    setEmployees(await hr.get('/employees', { company_id: companyId }));
  };
  useEffect(() => { reload(); }, [companyId]);

  const saveGoal = async () => {
    try {
      if (!gForm.employee_id || !gForm.title) return toast.error('Employee and title required');
      await hr.post('/goals', { ...gForm, weightage: Number(gForm.weightage || 0) });
      toast.success('Goal added'); setOpenG(false); setGForm({ category: 'kra' }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const saveCycle = async () => {
    try {
      if (!cyForm.name) return toast.error('Name required');
      await hr.post('/review-cycles', { ...cyForm, company_id: companyId });
      toast.success('Cycle created'); setOpenCy(false); setCyForm({ review_type: 'annual' }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e.full_name]));

  return (
    <div data-testid="hr-performance">
      <PageHeader title="Performance" subtitle="KRAs · KPIs · review cycles"
        right={<div className="flex gap-2"><Btn variant="ghost" onClick={() => setOpenCy(true)}>+ Cycle</Btn><Btn variant="accent" onClick={() => setOpenG(true)} data-testid="hr-new-goal">+ Add Goal</Btn></div>} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className={`${SOFT} rounded-xl p-4`}>
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-2">Review Cycles</div>
          {!cycles.length ? <p className="text-white/40 italic text-sm">No cycles yet.</p> : cycles.map((c) => (
            <div key={c.id} className="py-2 border-t border-white/5">
              <div className="text-white text-sm">{c.name}</div>
              <div className="text-xs text-white/45">{c.review_type} · {fmtDate(c.start_date)} → {fmtDate(c.end_date)}</div>
            </div>
          ))}
        </div>
        <div className={`${SOFT} rounded-xl p-4`}>
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-2">Goal Distribution</div>
          <div className="text-3xl font-semibold text-white">{goals.length}</div>
          <div className="text-xs text-white/45">total goals across team</div>
        </div>
      </div>

      <Table
        columns={[
          { key: 'employee_id', label: 'Employee', render: (r) => empMap[r.employee_id] || '—' },
          { key: 'title', label: 'Goal' },
          { key: 'category', label: 'Type', render: (r) => <Pill tone={r.category === 'kra' ? 'green' : r.category === 'kpi' ? 'blue' : 'orange'}>{r.category}</Pill> },
          { key: 'weightage', label: 'Weight', align: 'right', render: (r) => `${r.weightage}%` },
          { key: 'progress', label: 'Progress', align: 'right', render: (r) => `${r.progress}%` },
          { key: 'status', label: 'Status', render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> },
        ]}
        rows={goals} empty="No goals yet."
      />

      <Drawer open={openG} onClose={() => setOpenG(false)} title="Add Goal" width="max-w-lg">
        <div className="space-y-3">
          <Select label="Employee *" value={gForm.employee_id || ''} onChange={(e) => setGForm({ ...gForm, employee_id: e.target.value })}>
            <option value="">—</option>{employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </Select>
          <Input label="Title *" value={gForm.title || ''} onChange={(e) => setGForm({ ...gForm, title: e.target.value })} />
          <Select label="Category" value={gForm.category} onChange={(e) => setGForm({ ...gForm, category: e.target.value })}>
            <option value="kra">KRA</option><option value="kpi">KPI</option><option value="development">Development</option>
          </Select>
          <Input label="Weight (%)" type="number" value={gForm.weightage || 0} onChange={(e) => setGForm({ ...gForm, weightage: e.target.value })} />
          <Input label="Due Date" type="date" value={gForm.due_date || ''} onChange={(e) => setGForm({ ...gForm, due_date: e.target.value })} />
          <Textarea label="Target / Description" value={gForm.target || ''} onChange={(e) => setGForm({ ...gForm, target: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpenG(false)}>Cancel</Btn><Btn variant="accent" onClick={saveGoal}>Save</Btn></div>
      </Drawer>

      <Drawer open={openCy} onClose={() => setOpenCy(false)} title="New Review Cycle" width="max-w-lg">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name *" value={cyForm.name || ''} onChange={(e) => setCyForm({ ...cyForm, name: e.target.value })} />
          <Select label="Type" value={cyForm.review_type} onChange={(e) => setCyForm({ ...cyForm, review_type: e.target.value })}>
            <option value="annual">Annual</option><option value="half_yearly">Half-yearly</option>
            <option value="quarterly">Quarterly</option><option value="probation">Probation</option>
          </Select>
          <Input label="Start" type="date" value={cyForm.start_date || ''} onChange={(e) => setCyForm({ ...cyForm, start_date: e.target.value })} />
          <Input label="End" type="date" value={cyForm.end_date || ''} onChange={(e) => setCyForm({ ...cyForm, end_date: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpenCy(false)}>Cancel</Btn><Btn variant="accent" onClick={saveCycle}>Save</Btn></div>
      </Drawer>
    </div>
  );
}
